import { useGetAllRolesQuery } from 'store/services/core/roles';
import { useCallback, useMemo } from 'react';

import type { RoleWithTags } from 'types';

interface UseRolesWithTagsResult {
  roles: RoleWithTags[];
  tags: string[];
  isLoading: boolean;
  resolveRoleValue: (value?: string) => string | undefined;
  isRoleOrTagValue: (value?: string) => boolean;
}

/**
 * Returns all Core roles with their tags (štítky). Used by the condition editor
 * to offer roles and their tags as values for the "UserRole" system datasource,
 * where the stored static value is either a role GUID or a tag name.
 */
export const useRolesWithTags = (enabled = true): UseRolesWithTagsResult => {
  const { data: allRolesData, isFetching } = useGetAllRolesQuery(
    {
      page: 1,
      pageSize: 1000,
      orderBy: 'id',
      orderDesc: false,
      search: '',
    },
    { skip: !enabled }
  );

  const roles = useMemo<RoleWithTags[]>(
    () =>
      (allRolesData?.items ?? []).map((role) => ({
        id: role.id,
        name: role.name,
        tags: role.tags ?? [],
      })),
    [allRolesData?.items]
  );

  const tags = useMemo(() => Array.from(new Set(roles.flatMap((role) => role.tags))).sort(), [roles]);

  const resolveRoleValue = useCallback(
    (value?: string) => {
      if (!value) return value;

      return roles.find((role) => role.id.toLowerCase() === value.toLowerCase())?.name ?? value;
    },
    [roles]
  );

  const isRoleOrTagValue = useCallback(
    (value?: string) => {
      if (!value) return false;

      const normalized = value.toLowerCase();
      return roles.some((role) => role.id.toLowerCase() === normalized) || tags.some((tag) => tag.toLowerCase() === normalized);
    },
    [roles, tags]
  );

  return {
    roles,
    tags,
    isLoading: isFetching,
    resolveRoleValue,
    isRoleOrTagValue,
  };
};
